import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronDown } from "lucide-react";

const FiltersPanel = ({
  sidebarOpen,
  setSidebarOpen,
  viewType,
  users = [],
  trainings = [],
  userQualifications = [],
  userEvents = [],
  onFiltersChange
}) => {
  // Refs for dropdown containers
  const roleDropdownRef = useRef(null);
  const siteDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const managerDropdownRef = useRef(null);
  const supervisorDropdownRef = useRef(null);
  const coordinatorDropdownRef = useRef(null);
  const qualificationDropdownRef = useRef(null);
  const trainingCenterDropdownRef = useRef(null);
  const createdByDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  // User filter states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [selectedCoordinators, setSelectedCoordinators] = useState([]);
  const [showCertificateIssues, setShowCertificateIssues] = useState(false);
  
  // Search terms for user filters
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [siteSearchTerm, setSiteSearchTerm] = useState("");
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [managerSearchTerm, setManagerSearchTerm] = useState("");
  const [supervisorSearchTerm, setSupervisorSearchTerm] = useState("");
  const [coordinatorSearchTerm, setCoordinatorSearchTerm] = useState("");
  
  // Dropdown states for user filters
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const [supervisorDropdownOpen, setSupervisorDropdownOpen] = useState(false);
  const [coordinatorDropdownOpen, setCoordinatorDropdownOpen] = useState(false);

  // Training filter states
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  const [selectedTrainingCenters, setSelectedTrainingCenters] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [trainingDateRange, setTrainingDateRange] = useState({ start: '', end: '' });
  
  // Search terms for training filters
  const [qualificationSearchTerm, setQualificationSearchTerm] = useState("");
  const [trainingCenterSearchTerm, setTrainingCenterSearchTerm] = useState("");
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState("");
  
  // Dropdown states for training filters
  const [qualificationDropdownOpen, setQualificationDropdownOpen] = useState(false);
  const [trainingCenterDropdownOpen, setTrainingCenterDropdownOpen] = useState(false);
  const [createdByDropdownOpen, setCreatedByDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Generate unique data for filters from users
  const getUniqueRoles = useMemo(() => {
    return users
      .filter(user => user.training_role_data && user.training_role_data.name)
      .map(user => user.training_role_data)
      .filter((role, index, self) => 
        index === self.findIndex(r => r.id === role.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const getUniqueSites = useMemo(() => {
    return users
      .filter(user => user.training_site_data && user.training_site_data.name)
      .map(user => user.training_site_data)
      .filter((site, index, self) => 
        index === self.findIndex(s => s.id === site.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const getUniqueCountries = useMemo(() => {
    return users
      .filter(user => user.training_country_data && user.training_country_data.name)
      .map(user => user.training_country_data)
      .filter((country, index, self) => 
        index === self.findIndex(c => c.id === country.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const getUniqueManagers = useMemo(() => {
    return users
      .filter(user => user.training_manager_data && user.training_manager_data.first_name)
      .map(user => user.training_manager_data)
      .filter((manager, index, self) => 
        index === self.findIndex(m => m.id === manager.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  const getUniqueSupervisors = useMemo(() => {
    return users
      .filter(user => user.training_supervisor_data && user.training_supervisor_data.first_name)
      .map(user => user.training_supervisor_data)
      .filter((supervisor, index, self) => 
        index === self.findIndex(s => s.id === supervisor.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  const getUniqueCoordinators = useMemo(() => {
    return users
      .filter(user => user.training_coordinator_data && user.training_coordinator_data.first_name)
      .map(user => user.training_coordinator_data)
      .filter((coordinator, index, self) => 
        index === self.findIndex(c => c.id === coordinator.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  // Generate unique data for training filters
  const getUniqueQualifications = useMemo(() => {
    return trainings
      .filter(training => training.qualification_name)
      .map(training => ({ id: training.qualification_id, name: training.qualification_name }))
      .filter((qualification, index, self) => 
        index === self.findIndex(q => q.id === qualification.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [trainings]);

  const getUniqueTrainingCenters = useMemo(() => {
    return trainings
      .filter(training => training.training_center_name)
      .map(training => ({ id: training.training_center_id, name: training.training_center_name }))
      .filter((center, index, self) => 
        index === self.findIndex(c => c.id === center.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [trainings]);

  const getUniqueCreatedBy = useMemo(() => {
    return trainings
      .filter(training => training.created_by)
      .map(training => training.created_by)
      .filter((createdBy, index, self) => 
        index === self.findIndex(cb => cb === createdBy)
      )
      .sort((a, b) => a.localeCompare(b));
  }, [trainings]);

  const getAvailableStatuses = useMemo(() => {
    return ['planned', 'approved', 'completed', 'cancelled'];
  }, []);

  // Filter functions with search
  const getFilteredRoles = () => {
    return getUniqueRoles.filter(role => 
      role.name.toLowerCase().includes(roleSearchTerm.toLowerCase())
    );
  };

  const getFilteredSites = () => {
    return getUniqueSites.filter(site => 
      site.name.toLowerCase().includes(siteSearchTerm.toLowerCase())
    );
  };

  const getFilteredCountries = () => {
    return getUniqueCountries.filter(country => 
      country.name.toLowerCase().includes(countrySearchTerm.toLowerCase())
    );
  };

  const getFilteredManagers = () => {
    return getUniqueManagers.filter(manager => 
      `${manager.first_name} ${manager.last_name}`.toLowerCase().includes(managerSearchTerm.toLowerCase())
    );
  };

  const getFilteredSupervisors = () => {
    return getUniqueSupervisors.filter(supervisor => 
      `${supervisor.first_name} ${supervisor.last_name}`.toLowerCase().includes(supervisorSearchTerm.toLowerCase())
    );
  };

  const getFilteredCoordinators = () => {
    return getUniqueCoordinators.filter(coordinator => 
      `${coordinator.first_name} ${coordinator.last_name}`.toLowerCase().includes(coordinatorSearchTerm.toLowerCase())
    );
  };

  const getFilteredQualifications = () => {
    return getUniqueQualifications.filter(qualification => 
      qualification.name.toLowerCase().includes(qualificationSearchTerm.toLowerCase())
    );
  };

  const getFilteredTrainingCenters = () => {
    return getUniqueTrainingCenters.filter(center => 
      center.name.toLowerCase().includes(trainingCenterSearchTerm.toLowerCase())
    );
  };

  const getFilteredCreatedBy = () => {
    return getUniqueCreatedBy.filter(createdBy => 
      createdBy.toLowerCase().includes(createdBySearchTerm.toLowerCase())
    );
  };

  // Toggle functions
  const toggleRole = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleSite = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const toggleCountry = (countryId) => {
    setSelectedCountries(prev => 
      prev.includes(countryId) ? prev.filter(id => id !== countryId) : [...prev, countryId]
    );
  };

  const toggleManager = (managerId) => {
    setSelectedManagers(prev => 
      prev.includes(managerId) ? prev.filter(id => id !== managerId) : [...prev, managerId]
    );
  };

  const toggleSupervisor = (supervisorId) => {
    setSelectedSupervisors(prev => 
      prev.includes(supervisorId) ? prev.filter(id => id !== supervisorId) : [...prev, supervisorId]
    );
  };

  const toggleCoordinator = (coordinatorId) => {
    setSelectedCoordinators(prev => 
      prev.includes(coordinatorId) ? prev.filter(id => id !== coordinatorId) : [...prev, coordinatorId]
    );
  };

  const toggleQualification = (qualificationId) => {
    setSelectedQualifications(prev => 
      prev.includes(qualificationId) ? prev.filter(id => id !== qualificationId) : [...prev, qualificationId]
    );
  };

  const toggleTrainingCenter = (centerId) => {
    setSelectedTrainingCenters(prev => 
      prev.includes(centerId) ? prev.filter(id => id !== centerId) : [...prev, centerId]
    );
  };

  const toggleCreatedBy = (createdBy) => {
    setSelectedCreatedBy(prev => 
      prev.includes(createdBy) ? prev.filter(cb => cb !== createdBy) : [...prev, createdBy]
    );
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    if (viewType === 'users') {
      setSelectedRoles([]);
      setSelectedSites([]);
      setSelectedCountries([]);
      setSelectedManagers([]);
      setSelectedSupervisors([]);
      setSelectedCoordinators([]);
      setRoleSearchTerm("");
      setSiteSearchTerm("");
      setCountrySearchTerm("");
      setManagerSearchTerm("");
      setSupervisorSearchTerm("");
      setCoordinatorSearchTerm("");
      setShowCertificateIssues(false);
      setRoleDropdownOpen(false);
      setSiteDropdownOpen(false);
      setCountryDropdownOpen(false);
      setManagerDropdownOpen(false);
      setSupervisorDropdownOpen(false);
      setCoordinatorDropdownOpen(false);
    } else {
      setSelectedQualifications([]);
      setSelectedTrainingCenters([]);
      setSelectedCreatedBy([]);
      setSelectedStatuses([]);
      setTrainingDateRange({ start: '', end: '' });
      setQualificationSearchTerm("");
      setTrainingCenterSearchTerm("");
      setCreatedBySearchTerm("");
      setQualificationDropdownOpen(false);
      setTrainingCenterDropdownOpen(false);
      setCreatedByDropdownOpen(false);
      setStatusDropdownOpen(false);
    }
  };

  // Effect to call onFiltersChange when filters change
  useEffect(() => {
    if (onFiltersChange) {
      const filterState = {
        selectedRoles,
        selectedSites,
        selectedCountries,
        selectedManagers,
        selectedSupervisors,
        selectedCoordinators,
        selectedQualifications,
        selectedTrainingCenters,
        selectedCreatedBy,
        selectedStatuses,
        trainingDateRange,
        showCertificateIssues
      };
      onFiltersChange(filterState);
    }
  }, [
    selectedRoles, selectedSites, selectedCountries, selectedManagers, selectedSupervisors, selectedCoordinators,
    selectedQualifications, selectedTrainingCenters, selectedCreatedBy, selectedStatuses, trainingDateRange, showCertificateIssues,
    onFiltersChange
  ]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setRoleDropdownOpen(false);
      }
      if (siteDropdownRef.current && !siteDropdownRef.current.contains(event.target)) {
        setSiteDropdownOpen(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target)) {
        setManagerDropdownOpen(false);
      }
      if (supervisorDropdownRef.current && !supervisorDropdownRef.current.contains(event.target)) {
        setSupervisorDropdownOpen(false);
      }
      if (coordinatorDropdownRef.current && !coordinatorDropdownRef.current.contains(event.target)) {
        setCoordinatorDropdownOpen(false);
      }
      if (qualificationDropdownRef.current && !qualificationDropdownRef.current.contains(event.target)) {
        setQualificationDropdownOpen(false);
      }
      if (trainingCenterDropdownRef.current && !trainingCenterDropdownRef.current.contains(event.target)) {
        setTrainingCenterDropdownOpen(false);
      }
      if (createdByDropdownRef.current && !createdByDropdownRef.current.contains(event.target)) {
        setCreatedByDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    // Add event listener when component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {sidebarOpen && (
        <>
          <div 
            className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 overflow-y-auto transition-transform duration-300"
            onMouseLeave={() => setSidebarOpen(false)}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
              </div>

              {/* Clear All Filters */}
              <div className="mb-6">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-brand-500 hover:text-brand-600 font-medium"
                >
                  Clear All Filters
                </button>
              </div>

              {/* User Filters - only show in Users view */}
              {viewType === 'users' && (
                <>
                  {/* Roles Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Roles</h3>
                <div className="relative dropdown-container" ref={roleDropdownRef}>
                  <button
                    onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedRoles.length > 0 
                        ? `${selectedRoles.length} selected`
                        : "Select roles..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${roleDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {roleDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search roles..."
                          value={roleSearchTerm}
                          onChange={(e) => setRoleSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredRoles().map(role => (
                          <label key={role.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedRoles.includes(role.id)}
                              onChange={() => toggleRole(role.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">{role.name}</span>
                          </label>
                        ))}
                        {getFilteredRoles().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No roles found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sites Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Sites</h3>
                <div className="relative dropdown-container" ref={siteDropdownRef}>
                  <button
                    onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedSites.length > 0 
                        ? `${selectedSites.length} selected`
                        : "Select sites..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${siteDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {siteDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search sites..."
                          value={siteSearchTerm}
                          onChange={(e) => setSiteSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredSites().map(site => (
                          <label key={site.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedSites.includes(site.id)}
                              onChange={() => toggleSite(site.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">{site.name}</span>
                          </label>
                        ))}
                        {getFilteredSites().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No sites found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Countries Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Countries</h3>
                <div className="relative dropdown-container" ref={countryDropdownRef}>
                  <button
                    onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedCountries.length > 0 
                        ? `${selectedCountries.length} selected`
                        : "Select countries..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {countryDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search countries..."
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredCountries().map(country => (
                          <label key={country.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedCountries.includes(country.id)}
                              onChange={() => toggleCountry(country.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">{country.name}</span>
                          </label>
                        ))}
                        {getFilteredCountries().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No countries found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Managers Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Managers</h3>
                <div className="relative dropdown-container" ref={managerDropdownRef}>
                  <button
                    onClick={() => setManagerDropdownOpen(!managerDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedManagers.length > 0 
                        ? `${selectedManagers.length} selected`
                        : "Select managers..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${managerDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {managerDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search managers..."
                          value={managerSearchTerm}
                          onChange={(e) => setManagerSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredManagers().map(manager => (
                          <label key={manager.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedManagers.includes(manager.id)}
                              onChange={() => toggleManager(manager.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">
                              {manager.first_name} {manager.last_name}
                            </span>
                          </label>
                        ))}
                        {getFilteredManagers().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No managers found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Supervisors Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Supervisors</h3>
                <div className="relative dropdown-container" ref={supervisorDropdownRef}>
                  <button
                    onClick={() => setSupervisorDropdownOpen(!supervisorDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedSupervisors.length > 0 
                        ? `${selectedSupervisors.length} selected`
                        : "Select supervisors..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${supervisorDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {supervisorDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search supervisors..."
                          value={supervisorSearchTerm}
                          onChange={(e) => setSupervisorSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredSupervisors().map(supervisor => (
                          <label key={supervisor.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedSupervisors.includes(supervisor.id)}
                              onChange={() => toggleSupervisor(supervisor.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">
                              {supervisor.first_name} {supervisor.last_name}
                            </span>
                          </label>
                        ))}
                        {getFilteredSupervisors().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No supervisors found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Coordinators Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Coordinators</h3>
                <div className="relative dropdown-container" ref={coordinatorDropdownRef}>
                  <button
                    onClick={() => setCoordinatorDropdownOpen(!coordinatorDropdownOpen)}
                    className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                  >
                    <span className="text-sm text-slate-700">
                      {selectedCoordinators.length > 0 
                        ? `${selectedCoordinators.length} selected`
                        : "Select coordinators..."
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${coordinatorDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {coordinatorDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                      <div className="p-2 border-b border-steel-200">
                        <input
                          type="text"
                          placeholder="Search coordinators..."
                          value={coordinatorSearchTerm}
                          onChange={(e) => setCoordinatorSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredCoordinators().map(coordinator => (
                          <label key={coordinator.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedCoordinators.includes(coordinator.id)}
                              onChange={() => toggleCoordinator(coordinator.id)}
                              className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                            />
                            <span className="text-sm text-slate-700">
                              {coordinator.first_name} {coordinator.last_name}
                            </span>
                          </label>
                        ))}
                        {getFilteredCoordinators().length === 0 && (
                          <p className="text-sm text-slate-500 px-3 py-2">No coordinators found</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Certificate Issues Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Certificate Status</h3>
                <div className="flex items-center">
                  <input
                    id="show-certificate-issues"
                    type="checkbox"
                    checked={showCertificateIssues}
                    onChange={(e) => setShowCertificateIssues(e.target.checked)}
                    className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded"
                  />
                  <label 
                    htmlFor="show-certificate-issues"
                    className="ml-3 text-sm font-medium text-slate-700 cursor-pointer"
                  >
                    Show certificate issues only
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1 ml-7">
                  Shows users with expiring or missing certificates for their role
                </p>
              </div>
                </>
              )}

              {/* Training Filters - only show in Trainings view */}
              {viewType === 'trainings' && (
                <>
                  {/* Qualification Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Qualification</h3>
                    <div className="relative dropdown-container" ref={qualificationDropdownRef}>
                      <button
                        onClick={() => setQualificationDropdownOpen(!qualificationDropdownOpen)}
                        className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                      >
                        <span className="text-sm text-slate-700">
                          {selectedQualifications.length > 0 
                            ? `${selectedQualifications.length} selected`
                            : "Select qualifications..."
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${qualificationDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {qualificationDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                          <div className="p-2 border-b border-steel-200">
                            <input
                              type="text"
                              placeholder="Search qualifications..."
                              value={qualificationSearchTerm}
                              onChange={(e) => setQualificationSearchTerm(e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {getFilteredQualifications().map(qualification => (
                              <label key={qualification.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedQualifications.includes(qualification.id)}
                                  onChange={() => toggleQualification(qualification.id)}
                                  className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                                />
                                <span className="text-sm text-slate-700">
                                  {qualification.name}
                                </span>
                              </label>
                            ))}
                            {getFilteredQualifications().length === 0 && (
                              <p className="text-sm text-slate-500 px-3 py-2">No qualifications found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Training Center Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Training Center</h3>
                    <div className="relative dropdown-container" ref={trainingCenterDropdownRef}>
                      <button
                        onClick={() => setTrainingCenterDropdownOpen(!trainingCenterDropdownOpen)}
                        className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                      >
                        <span className="text-sm text-slate-700">
                          {selectedTrainingCenters.length > 0 
                            ? `${selectedTrainingCenters.length} selected`
                            : "Select training centers..."
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${trainingCenterDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {trainingCenterDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                          <div className="p-2 border-b border-steel-200">
                            <input
                              type="text"
                              placeholder="Search training centers..."
                              value={trainingCenterSearchTerm}
                              onChange={(e) => setTrainingCenterSearchTerm(e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {getFilteredTrainingCenters().map(center => (
                              <label key={center.id} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedTrainingCenters.includes(center.id)}
                                  onChange={() => toggleTrainingCenter(center.id)}
                                  className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                                />
                                <span className="text-sm text-slate-700">
                                  {center.name}
                                </span>
                              </label>
                            ))}
                            {getFilteredTrainingCenters().length === 0 && (
                              <p className="text-sm text-slate-500 px-3 py-2">No training centers found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Created By Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Created By</h3>
                    <div className="relative dropdown-container" ref={createdByDropdownRef}>
                      <button
                        onClick={() => setCreatedByDropdownOpen(!createdByDropdownOpen)}
                        className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                      >
                        <span className="text-sm text-slate-700">
                          {selectedCreatedBy.length > 0 
                            ? `${selectedCreatedBy.length} selected`
                            : "Select creators..."
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${createdByDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {createdByDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                          <div className="p-2 border-b border-steel-200">
                            <input
                              type="text"
                              placeholder="Search creators..."
                              value={createdBySearchTerm}
                              onChange={(e) => setCreatedBySearchTerm(e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {getFilteredCreatedBy().map(createdBy => (
                              <label key={createdBy} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedCreatedBy.includes(createdBy)}
                                  onChange={() => toggleCreatedBy(createdBy)}
                                  className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                                />
                                <span className="text-sm text-slate-700">
                                  {createdBy}
                                </span>
                              </label>
                            ))}
                            {getFilteredCreatedBy().length === 0 && (
                              <p className="text-sm text-slate-500 px-3 py-2">No creators found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Status</h3>
                    <div className="relative dropdown-container" ref={statusDropdownRef}>
                      <button
                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                        className="w-full px-3 py-2 text-left bg-white border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between"
                      >
                        <span className="text-sm text-slate-700">
                          {selectedStatuses.length > 0 
                            ? `${selectedStatuses.length} selected`
                            : "Select statuses..."
                          }
                        </span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {statusDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg">
                          <div className="max-h-48 overflow-y-auto">
                            {getAvailableStatuses.map(status => (
                              <label key={status} className="flex items-center px-3 py-2 hover:bg-brand-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedStatuses.includes(status)}
                                  onChange={() => toggleStatus(status)}
                                  className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-3"
                                />
                                <span className="text-sm text-slate-700">
                                  {status}
                                </span>
                              </label>
                            ))}
                            {getAvailableStatuses.length === 0 && (
                              <p className="text-sm text-slate-500 px-3 py-2">No statuses found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date Range Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Date Range</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={trainingDateRange.start}
                          onChange={(e) => setTrainingDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">End Date</label>
                        <input
                          type="date"
                          value={trainingDateRange.end}
                          onChange={(e) => setTrainingDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                        />
                      </div>
                    </div>
                  </div>

                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FiltersPanel;